import React, { useState, createContext } from 'react';
import PropTypes from 'prop-types';
import { Typography, Switch, Row, Col } from 'antd';
import AntdProForm, {
  ProFormText,
  ProFormSelect,
  ProFormRadio,
  ProFormDependency,
  ProFormList,
  ProFormGroup,
} from '@ant-design/pro-form';
import './UserForm.less';
import ProForm from '../../layout/ProForm';
import { useQuery } from '@apollo/client';
import { GET_FACULTIES } from '../../../gql/faculties';
import { GET_ROLES } from '../../../gql/roles';
import UpdateAuthenticationModeModalForm from './UpdateAuthenticationModeModalForm';
import SearchUserFromHroModalForm from './SearchUserFromHroModalForm';
import { SelectDepartment, SelectFaculty } from 'components/input';

export const selectedHroStaffContext = createContext();
const { Text } = Typography;
const isFacultyAndDepartmentMatch = ({
  selectedFaculty,
  selectedDepartment,
  faculties,
}) => {
  if (!selectedDepartment) return true;
  if (!selectedFaculty) return false;
  const faculty = faculties.find(({ id }) => id === selectedFaculty);
  return faculty.departments.some(({ id }) => id === selectedDepartment);
};
const mapAuthModeToText = (authMode) => {
  switch (authMode) {
    case 'DIRECTORY':
      return 'PolyU SSO Directory';
    case 'PASSWORD':
      return 'Password';
    default:
      return 'Password';
  }
};

const UserForm = ({ onFinish, initialValues, isEdit }) => {
  const [selectedHroStaff, setSelectedHroStaff] = useState(null);
  const { data, loading: faculitiesLoading } = useQuery(GET_FACULTIES);
  const { data: getRoles, loading: rolesLoading } = useQuery(GET_ROLES);
  const [form] = AntdProForm.useForm();

  return (
    !faculitiesLoading &&
    !rolesLoading && (
      <selectedHroStaffContext.Provider
        value={{ selectedHroStaff, setSelectedHroStaff }}
      >
        <SearchUserFromHroModalForm
          postFinish={() => {
            form.setFieldsValue({
              department: selectedHroStaff?.department?.id,
              faculty: selectedHroStaff?.faculty?.id,
              netId: selectedHroStaff?.netId,
              name: selectedHroStaff?.name,
              title: selectedHroStaff?.title,
              email: selectedHroStaff?.email,
              authenticationMode: 'DIRECTORY',
            });
            form.setFields([
              {
                name: ['roles', 0, 'faculty'],
                value: selectedHroStaff?.faculty?.id,
              },
              {
                name: ['roles', 0, 'department'],
                value: selectedHroStaff?.department?.id,
              },
            ]);
          }}
        />
        <ProForm
          form={form}
          onFinish={onFinish}
          onValuesChange={(changedValues) => {
            // to clean the department of roles if faculty of roles change
            if (changedValues['roles']) {
              const { roles } = form.getFieldsValue();
              const changedRoles = changedValues['roles'];
              const lastIdx = changedRoles.length - 1;

              // as the changed roles always appear in the last, check roles[lastIdx] faculty and department match?
              // equals to undefined if it is a new row
              if (roles[lastIdx] === undefined) return;

              if (
                !isFacultyAndDepartmentMatch({
                  selectedFaculty: roles[lastIdx].faculty,
                  selectedDepartment: roles[lastIdx].department,
                  faculties: data.faculties,
                })
              ) {
                form.setFields([
                  { name: ['roles', lastIdx, 'department'], value: undefined },
                ]);
              }
            }
          }}
          initialValues={initialValues}
        >
          <SelectFaculty />
          <SelectDepartment form={form} />
          <ProFormText
            width="xl"
            name="title"
            label="Title"
            placeholder="title"
          />
          <ProFormText
            width="xl"
            name="name"
            label="Staff name"
            placeholder="name"
            rules={[{ required: true, message: 'Please enter staff name!' }]}
          />
          <ProFormText
            width="xl"
            name="netId"
            label="netID"
            placeholder="netID"
            rules={[{ required: true, message: 'Please enter netID!' }]}
          />
          <ProFormText
            width="xl"
            name="email"
            label="Email"
            placeholder="email"
            rules={[
              { required: true, message: 'Please enter email!' },
              {
                type: 'email',
                message: 'This is not valid email!',
              },
            ]}
          />
          {isEdit && (
            <AntdProForm.Item
              name="active"
              label="Active"
              valuePropName="checked"
            >
              <Switch />
            </AntdProForm.Item>
          )}
          {!isEdit ? (
            <ProFormRadio.Group
              key="authenticationMode"
              name="authenticationMode"
              radioType="button"
              label="Authentication mode"
              disabled={isEdit}
              options={[
                {
                  label: 'PolyU SSO Directory',
                  value: 'DIRECTORY',
                },
                {
                  label: 'Password',
                  value: 'PASSWORD',
                },
              ]}
              rules={[
                {
                  required: !isEdit,
                  message: 'Please select!',
                },
              ]}
            />
          ) : (
            <Row style={{ marginBottom: '24px' }}>
              <Col span={24} style={{ paddingBottom: '8px' }}>
                <Text>Authentication Mode</Text>
              </Col>
              <Col span={24}>
                <Text type="secondary">
                  {mapAuthModeToText(initialValues.authenticationMode)}
                </Text>
                <UpdateAuthenticationModeModalForm />
              </Col>
            </Row>
          )}

          {!isEdit && (
            <ProFormDependency name={['authenticationMode']}>
              {({ authenticationMode }) => {
                return (
                  <ProFormText
                    width="xl"
                    name="password"
                    label="Password"
                    placeholder="password"
                    disabled={isEdit || authenticationMode !== 'PASSWORD'}
                    rules={[
                      {
                        required: authenticationMode === 'PASSWORD' && !isEdit,
                        message: 'Please enter password!',
                      },
                    ]}
                  />
                );
              }}
            </ProFormDependency>
          )}
          <ProFormList
            name="roles"
            label="Roles"
            creatorButtonProps={{
              creatorButtonText: 'Add role',
              position: 'bottom',
              type: 'dashed',
            }}
            copyIconProps={false}
            deleteIconProps={{
              tooltipText: 'delete',
            }}
          >
            <ProFormGroup>
              <ProFormSelect
                showSearch
                label="Role"
                name="role"
                width="sm"
                placeholder="role"
                rules={[{ required: true, message: 'please select role' }]}
                valueEnum={
                  getRoles
                    ? getRoles.roles.reduce((acc, { id, name }) => {
                        acc[id] = name;
                        return acc;
                      }, {})
                    : {}
                }
              />
              <ProFormSelect
                showSearch
                label="Faculty"
                name="faculty"
                placeholder="faculty"
                width="sm"
                valueEnum={
                  data
                    ? data.faculties.reduce(
                        (acc, { id, name, abbreviation }) => {
                          acc[id] = `${abbreviation} - ${name}`;
                          return acc;
                        },
                        {}
                      )
                    : {}
                }
              />
              <ProFormDependency name={['faculty']}>
                {({ faculty }) => {
                  const departemnts = faculty
                    ? data.faculties.find(({ id }) => id === faculty)
                        ?.departments
                    : [];

                  return (
                    <ProFormSelect
                      showSearch
                      label="Department"
                      name="department"
                      placeholder="department"
                      width="sm"
                      valueEnum={departemnts?.reduce(
                        (acc, { id, name, abbreviation }) => {
                          acc[id] = `${abbreviation} - ${name}`;
                          return acc;
                        },
                        {}
                      )}
                    />
                  );
                }}
              </ProFormDependency>
            </ProFormGroup>
          </ProFormList>
        </ProForm>
      </selectedHroStaffContext.Provider>
    )
  );
};

UserForm.defaultProps = {
  initialValues: {},
  isEdit: false,
};

UserForm.propTypes = {
  onFinish: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({
    netId: PropTypes.string,
    name: PropTypes.string,
    authenticationMode: PropTypes.string,
    password: PropTypes.string,
    email: PropTypes.string,
    faculty: PropTypes.string,
    department: PropTypes.string,
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        role: PropTypes.string,
        department: PropTypes.string,
        faculty: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default UserForm;
